'use client';

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { XCircleIcon } from 'lucide-react';
import * as React from 'react';

import ArrowDown from '@/shared/assets/chevron-down.svg?react';
import { Gradients } from '@/shared/components/theme/gradient';
import { LabelInput } from '@/shared/components/ui/label-input';
import { cn } from '@/shared/utils/cn';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value> & {
    asLabelInput: boolean;
    label?: string;
    suffixIcon?: React.ReactNode;
    hideDropdownIcon?: boolean;
    placeholder?: string;
  }
>(
  (
    {
      className,
      children,
      asLabelInput,
      label,
      placeholder,
      hideDropdownIcon,
      ...props
    },
    ref,
  ) => {
    if (asLabelInput) {
      return (
        <LabelInput
          label={label ?? ''}
          value={(children as string) ?? ''}
          placeholder={placeholder}
          readOnly
          className={
            props['aria-disabled'] ? '!cursor-not-allowed' : '!cursor-pointer'
          }
          suffixIcon={
            hideDropdownIcon ? undefined : (
              <ArrowDown className="size-4 text-white" />
            )
          }
        />
      );
    }

    return (
      <SelectPrimitive.Value
        ref={ref}
        className={cn('placeholder:text-white', className)}
        {...props}
        placeholder={placeholder}
      >
        {children}
      </SelectPrimitive.Value>
    );
  },
);
SelectValue.displayName = SelectPrimitive.Value.displayName;

const selectTriggerVariants = cva(
  'flex items-center justify-between whitespace-nowrap border px-3 py-2 shadow-sm ring-offset-background focus:outline-none focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-custom-light-grey [&>span]:line-clamp-1 border-translucent-white font-medium text-white',
  {
    variants: {
      variant: {
        default: 'bg-custom-soft-purple',
        transparent: 'bg-transparent ',
      },
      size: {
        default: 'h-14 text-sm rounded-xs',
        sm: 'h-8 text-2xs rounded-[6px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants> & {
      icon?: React.ReactNode;
      isOpen?: boolean;
      isInputTrigger?: boolean;
      onReset?: () => void;
      enableUnselect?: boolean;
    }
>(
  (
    {
      className,
      children,
      icon,
      isInputTrigger = false,
      variant,
      size,
      onReset,
      enableUnselect = false,
      ...props
    },
    ref,
  ) => (
    <div className="relative flex w-full">
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          !isInputTrigger && selectTriggerVariants({ variant, size }),
          'w-full',
          !isInputTrigger && 'overflow-x-clip text-ellipsis pr-6',
          className,
        )}
        {...props}
      >
        {children}
      </SelectPrimitive.Trigger>
      {!isInputTrigger && (
        <SelectPrimitive.Icon
          asChild
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer',
            !props.value && 'pointer-events-none',
          )}
        >
          {enableUnselect && props.value ? (
            <XCircleIcon
              onClick={onReset}
              className={cn(
                'size-4 font-medium text-white',
                size === 'sm' && 'size-3',
              )}
            />
          ) : (
            (icon ?? (
              <ArrowDown
                className={cn(
                  'size-4 font-medium text-white',
                  size === 'sm' && 'size-3',
                  props.isOpen ? 'rotate-180' : '',
                )}
              />
            ))
          )}
        </SelectPrimitive.Icon>
      )}
    </div>
  ),
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    withSeparators?: boolean;
  }
>(
  (
    {
      className,
      children,
      position = 'popper',
      withSeparators = true,
      ...props
    },
    ref,
  ) => {
    let content = children;

    if (withSeparators) {
      const childArray = React.Children.toArray(children);

      content = childArray.map((child, index) => {
        if (index === childArray.length - 1) {
          return child;
        }

        return (
          <React.Fragment key={index}>
            {child}
            <SelectSeparator />
          </React.Fragment>
        );
      });
    }

    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          ref={ref}
          className={cn(
            'relative z-50 max-h-96 w-full min-w-[8rem] overflow-hidden rounded-xs border-[0.5px] border-white/20 bg-dropdown-gray text-white shadow-md backdrop-blur-[30px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            position === 'popper' &&
              'data-[side=bottom]:translate-y-4 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
            className,
          )}
          position={position}
          {...props}
        >
          <div
            className={cn(
              Gradients.dropdownGradient,
              'pointer-events-none absolute inset-0 z-0 bg-gradient-to-br',
            )}
          />
          <SelectScrollUpButton className="relative z-[1]" />
          <SelectPrimitive.Viewport
            className={cn(
              'relative z-[1]',
              position === 'popper' &&
                'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
            )}
          >
            {content}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton className="relative z-[1]" />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  },
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold text-white', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    subLabel?: string;
  }
>(({ className, children, subLabel, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex h-[66px] w-full cursor-default select-none flex-col items-start justify-center rounded-sm text-base !text-white outline-none',
      'hover:bg-select-item-hover focus:bg-select-item-hover data-[state=checked]:bg-select-item-hover',
      'rounded-none text-white hover:rounded-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      'px-4 py-3',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <div className="flex flex-col">
      <SelectPrimitive.ItemText className="text-sm">
        {children}
      </SelectPrimitive.ItemText>
      {subLabel && (
        <span className="text-2xs text-custom-grey">{subLabel}</span>
      )}
    </div>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-0 h-px bg-white/30', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
